/**
 * RealEstatePage — CRUD de bienes raíces del tenant.
 *
 * Master data para DJ 1835. Cada inmueble se identifica por su ROL de avalúo
 * fiscal SII. Una vez creado, se puede asociar a documentos de venta (boleta /
 * factura de arriendo) para que la DJ 1835 lo detecte automáticamente.
 */
import { useMemo, useState } from 'react'
import { Plus, Pencil, Trash2, Home, AlertCircle } from 'lucide-react'
import { DataTable } from '@/components/ds/DataTable'

import {
  useRealEstates, useCreateRealEstate, useUpdateRealEstate, useDeleteRealEstate,
  type RealEstate, type RealEstatePayload, type DestinationCode, type PropertyTypeCode,
} from '../hooks/useRealEstate'
import { Modal } from '@/components/ds/Modal'

const DESTINATIONS: { value: DestinationCode; label: string }[] = [
  { value: 'habitacional', label: 'Habitacional' },
  { value: 'comercial', label: 'Comercial' },
  { value: 'industrial', label: 'Industrial' },
  { value: 'oficinas', label: 'Oficinas' },
  { value: 'bodegas', label: 'Bodegas' },
  { value: 'estacionamiento', label: 'Estacionamiento' },
  { value: 'agricola', label: 'Agrícola' },
  { value: 'otro', label: 'Otro' },
]

const PROPERTY_TYPES: { value: PropertyTypeCode; label: string }[] = [
  { value: 'no_agricola', label: 'No agrícola' },
  { value: 'dfl2', label: 'DFL N°2 (vivienda económica)' },
  { value: 'agricola', label: 'Agrícola' },
]

function emptyForm(): RealEstatePayload {
  return {
    rol_avaluo: '', alias: '', address: '', comuna: '', region: '',
    destination: 'comercial', property_type: 'no_agricola',
    avaluo_fiscal: '0', is_active: true, notes: '',
  }
}

export function RealEstatePage() {
  const { data, isLoading } = useRealEstates()
  const create = useCreateRealEstate()
  const remove = useDeleteRealEstate()

  const [editing, setEditing] = useState<RealEstate | null>(null)
  const [showForm, setShowForm] = useState(false)
  const [form, setForm] = useState<RealEstatePayload>(emptyForm())
  const [error, setError] = useState<string | null>(null)

  const items = data?.results || []

  const openCreate = () => {
    setEditing(null)
    setForm(emptyForm())
    setShowForm(true)
    setError(null)
  }

  const openEdit = (re: RealEstate) => {
    setEditing(re)
    setForm({
      rol_avaluo: re.rol_avaluo, alias: re.alias, address: re.address,
      comuna: re.comuna, region: re.region,
      destination: re.destination, property_type: re.property_type,
      avaluo_fiscal: re.avaluo_fiscal, is_active: re.is_active, notes: re.notes,
    })
    setShowForm(true)
    setError(null)
  }

  const updateMut = useUpdateRealEstate(editing?.id || '')

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    try {
      if (editing) {
        await updateMut.mutateAsync(form)
      } else {
        await create.mutateAsync(form)
      }
      setShowForm(false)
    } catch (err: any) {
      setError(err?.response?.data?.error?.message || 'Error al guardar')
    }
  }

  const onDelete = async (re: RealEstate) => {
    if (!confirm(`¿Eliminar el bien raíz ${re.rol_avaluo}?`)) return
    try {
      await remove.mutateAsync(re.id)
    } catch (err: any) {
      alert(err?.response?.data?.error?.message || 'No se pudo eliminar')
    }
  }

  return (
    <div className="p-6 space-y-6 max-w-7xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-content flex items-center gap-2">
            <Home size={20} /> Bienes Raíces
          </h1>
          <p className="text-sm text-content-subtle mt-1">
            Catálogo de inmuebles del tenant. Se asocian a documentos de venta para
            la Declaración Jurada N°1835 (rentas de arrendamiento percibidas).
          </p>
        </div>
        <button
          type="button"
          onClick={openCreate}
          className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold bg-primary text-on-primary rounded-lg hover:bg-primary-hover"
        >
          <Plus size={14} /> Nuevo
        </button>
      </div>

      {isLoading ? (
        <div className="text-content-muted">Cargando...</div>
      ) : items.length === 0 ? (
        <div className="rounded-card border border-divider bg-glass backdrop-blur-glass p-8 text-center text-content-muted shadow-card">
          Aún no hay bienes raíces registrados. Crea el primero para empezar.
        </div>
      ) : (
        <DataTable
          columns={[
            { key: 'rol_avaluo', header: 'ROL Avalúo', render: re => <span className="font-mono">{re.rol_avaluo}</span> },
            {
              key: 'address', header: 'Dirección',
              render: re => (
                <>
                  {re.alias && <div className="text-content-subtle text-xs">{re.alias}</div>}
                  {re.address}
                </>
              ),
            },
            { key: 'comuna', header: 'Comuna', render: re => <span className="text-content-subtle">{re.comuna}</span> },
            { key: 'destination_display', header: 'Destino', render: re => <span className="text-content-subtle">{re.destination_display}</span> },
            { key: 'property_type_display', header: 'Tipo', render: re => <span className="text-content-subtle">{re.property_type_display}</span> },
            {
              key: 'avaluo_fiscal', header: 'Avalúo Fiscal', numeric: true,
              sortValue: re => Number(re.avaluo_fiscal),
              render: re => Number(re.avaluo_fiscal).toLocaleString('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 }),
            },
            {
              key: 'acciones', header: '',
              render: re => (
                <div className="text-right">
                  <button onClick={() => openEdit(re)} className="text-primary hover:text-primary-hover mr-2" title="Editar">
                    <Pencil size={14} />
                  </button>
                  <button onClick={() => onDelete(re)} className="text-danger hover:text-danger-hover" title="Eliminar">
                    <Trash2 size={14} />
                  </button>
                </div>
              ),
            },
          ]}
          rows={items}
          rowKey={re => re.id}
          minWidth={860}
          caption="Bienes raíces registrados"
        />
      )}

      {showForm && (
        <Modal
          open
          onClose={() => setShowForm(false)}
          size="lg"
          title={editing ? 'Editar Bien Raíz' : 'Nuevo Bien Raíz'}
          actions={
            <>
              <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 text-sm border border-divider rounded-lg hover:bg-surface-hover">
                Cancelar
              </button>
              <button type="submit" form="bien-raiz-form" className="px-4 py-2 text-sm font-semibold bg-primary text-on-primary rounded-lg hover:bg-primary-hover">
                {editing ? 'Guardar' : 'Crear'}
              </button>
            </>
          }
        >
          {/* El pie lo pinta el Modal fuera de este <form>: el botón de guardar lo alcanza por `form=`. */}
          <form id="bien-raiz-form" onSubmit={onSubmit}>
          <div className="space-y-3">
            {error && (
              <div className="rounded border border-danger bg-danger/10 p-3 text-sm text-danger flex gap-2">
                <AlertCircle size={16} className="shrink-0 mt-0.5" /> {error}
              </div>
            )}
            <Field label="ROL Avalúo SII *" hint="Formato CCCCC-NNNN (ej: 13125-00123)">
              <input
                required
                value={form.rol_avaluo}
                onChange={(e) => setForm({ ...form, rol_avaluo: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-divider rounded bg-canvas text-content font-mono"
              />
            </Field>
            <Field label="Alias / Nombre interno">
              <input
                value={form.alias}
                onChange={(e) => setForm({ ...form, alias: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-divider rounded bg-canvas text-content"
              />
            </Field>
            <Field label="Dirección *">
              <input
                required
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-divider rounded bg-canvas text-content"
              />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Comuna *">
                <input
                  required
                  value={form.comuna}
                  onChange={(e) => setForm({ ...form, comuna: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-divider rounded bg-canvas text-content"
                />
              </Field>
              <Field label="Región">
                <input
                  value={form.region}
                  onChange={(e) => setForm({ ...form, region: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-divider rounded bg-canvas text-content"
                />
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Destino">
                <select
                  value={form.destination}
                  onChange={(e) => setForm({ ...form, destination: e.target.value as DestinationCode })}
                  className="w-full px-3 py-2 text-sm border border-divider rounded bg-canvas text-content"
                >
                  {DESTINATIONS.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                </select>
              </Field>
              <Field label="Tipo de propiedad">
                <select
                  value={form.property_type}
                  onChange={(e) => setForm({ ...form, property_type: e.target.value as PropertyTypeCode })}
                  className="w-full px-3 py-2 text-sm border border-divider rounded bg-canvas text-content"
                >
                  {PROPERTY_TYPES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
              </Field>
            </div>
            <Field label="Avalúo Fiscal (CLP)">
              <input
                type="number"
                step="1"
                value={form.avaluo_fiscal}
                onChange={(e) => setForm({ ...form, avaluo_fiscal: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-divider rounded bg-canvas text-content tabular-nums"
              />
            </Field>
            <Field label="Notas">
              <textarea
                rows={2}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-divider rounded bg-canvas text-content"
              />
            </Field>
          </div>
          </form>
        </Modal>
      )}
    </div>
  )
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold text-content-subtle mb-1">{label}</span>
      {children}
      {hint && <span className="block text-xs text-content-muted mt-1">{hint}</span>}
    </label>
  )
}

export default RealEstatePage
